import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "dropdown", "results", "placeholder", "selectButton", "verificationBadge"]
  static values = {
    url: String,
    minLength: { type: Number, default: 2 }
  }

  initialize() {
    this.submit = this.debounce(this.submit.bind(this), 300)
  }

  connect() {
    document.addEventListener('click', (event) => {
      if (!this.element.contains(event.target)) {
        this.hideResults()
      }
    })
    
    // Listen for company creation events
    document.addEventListener('company-created', this.handleCompanyCreated.bind(this))
  }

  focus() {
    this.showResults()
  }

  search(event) {
    const query = event.target.value.toLowerCase()
    this.submit(query)
    this.showResults()
  }

  submit(query) {
    fetch(`${this.urlValue}?query=${encodeURIComponent(query)}`)
      .then(response => response.json())
      .then(data => {
        this.resultsTarget.innerHTML = this.renderResults(data, query)
        this.showResults()
      })
      .catch(error => {
        console.error("Fetch failed:", error)
      })
  }

  renderResults(companies, query) {
    return `
      <div class="p-1 bg-white">
        <div class="flex flex-col">
          <!-- Results section -->
          <div class="flex flex-col gap-1">
            ${companies.length === 0 
              ? `<div class="flex items-center justify-center py-4 text-sm text-gray-500">
                   Įmonių nerasta
                 </div>`
              : companies.map(company => `
                  <button type="button"
                          class="w-full text-left px-3 py-1.5 text-sm rounded hover:bg-gray-50"
                          data-action="click->form-company-selector#selectCompany"
                          data-company-id="${company.id}"
                          data-company-name="${company.name}"
                          data-company-verified="${company.verified}"
                          data-company-type="${company.company_type}"
                          data-company-person-identity-missing="${company.person_identity_missing}"
                          data-company-tenant-owned="${company.is_tenant_company}">
                    ${company.name}
                  </button>
                `).join('')
            }
          </div>
        </div>
      </div>
    `;
  }

  selectCompany(event) {
    const name = event.currentTarget.dataset.companyName
    const id = event.currentTarget.dataset.companyId
    const verified = event.currentTarget.dataset.companyVerified === 'true'
    const companyType = event.currentTarget.dataset.companyType
    const personIdentityMissing = event.currentTarget.dataset.companyPersonIdentityMissing === 'true'
    const tenantOwned = event.currentTarget.dataset.companyTenantOwned === 'true'
    
    console.log('Company selected:', { name, id, verified })
    
    // Update placeholder text and set verification status
    this.placeholderTarget.textContent = name
    this.placeholderTarget.classList.remove('text-gray-500')
    this.placeholderTarget.classList.add('text-black', 'font-medium')
    
    const companyField = this.element.closest('.company-field')
    companyField.dataset.verified = verified
    companyField.dataset.hasCompany = 'true'

    // Find the badge container (label area above the company-field)
    const role = companyField.classList.contains('seller-field') ? 'seller' : 'buyer'
    const badgeContainer = companyField.parentElement.querySelector(`[data-badge-container="${role}"]`)

    // Remove existing badge if any
    if (badgeContainer) {
      const existingBadge = badgeContainer.querySelector('.company-role-badge')
      if (existingBadge) {
        existingBadge.remove()
      }
    }

    // Add verification badge only for unverified companies
    if (!verified && badgeContainer && (companyType !== 'individual' || personIdentityMissing || tenantOwned)) {
      const isIndividual = companyType === 'individual'
      const badge = document.createElement('button')
      badge.className = isIndividual ? 'company-role-badge company-role-badge--person' : 'company-role-badge'
      badge.setAttribute('type', 'button')
      badge.setAttribute('data-action', 'click->company-verification#openModal')
      badge.setAttribute('data-company-verification-company-id-param', id)
      badge.setAttribute('data-company-verification-company-role-param', role)
      badge.setAttribute('data-label', isIndividual ? (tenantOwned ? 'Patikrinti asmenį' : 'Papildyti duomenis') : 'Patvirtinti įmonę')

      badge.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-3 h-3">
          <path fill-rule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd" />
        </svg>
      `

      badgeContainer.appendChild(badge)
    }

    this.inputTarget.value = ""
    
    const hiddenInput = this.element.querySelector('input[type="hidden"]')
    if (hiddenInput) {
      hiddenInput.value = id
      
      // Refresh custom fields section
      const customFieldsFrame = document.getElementById('custom_fields')
      if (customFieldsFrame) {
        const currentUrl = new URL(window.location.href)
        const params = new URLSearchParams(currentUrl.search)
        params.set(hiddenInput.name.replace(/.*\[(.*)\]/, '$1'), id)
        
        const frameUrl = `${currentUrl.pathname}?${params.toString()}`
        customFieldsFrame.setAttribute('src', frameUrl)
      }
    }
    
    this.hideResults()
  }

  createNewCompany() {
    const query = this.inputTarget.value
    const companyVerificationController = this.application.getControllerForElementAndIdentifier(
      document.querySelector('[data-controller*="company-verification"]'),
      'company-verification'
    )
    
    if (companyVerificationController) {
      // Mark this selector as the one that triggered the modal
      this.element.dataset.creatingCompany = 'true'
      
      const button = document.createElement('button')
      button.dataset.companyVerificationCompanyNameParam = query || ''
      button.dataset.companyVerificationTriggerElementId = this.element.id || `selector_${Date.now()}`
      
      // Ensure element has an ID for tracking
      if (!this.element.id) {
        this.element.id = `selector_${Date.now()}`
      }
      
      companyVerificationController.openModal({ currentTarget: button })
    } else {
      console.error('Company verification controller not found!')
    }
  }

  toggleDropdown() {
    if (this.dropdownTarget.classList.contains('hidden')) {
      this.inputTarget.value = ""
      this.submit("")
      this.showDropdown()
      this.inputTarget.focus()
    } else {
      this.hideDropdown()
    }
  }

  showResults() {
    this.showDropdown()
  }

  hideResults() {
    this.hideDropdown()
  }

  showDropdown() {
    this.dropdownTarget.classList.remove('hidden')
  }

  hideDropdown() {
    this.dropdownTarget.classList.add('hidden')
  }

  handleCompanyCreated(event) {
    const { companyId, companyName, triggerElementId } = event.detail
    
    // Check if this is the specific selector that triggered the modal
    const isTargetSelector = triggerElementId && this.element.id === triggerElementId
    
    // Check if this selector should auto-select the new company
    // Prioritize the trigger selector, or any empty selector as fallback
    const hiddenInput = this.element.querySelector('input[type="hidden"]')
    const placeholder = this.placeholderTarget
    
    const isEmpty = hiddenInput && (!hiddenInput.value || hiddenInput.value === '') && 
                   placeholder && placeholder.textContent === 'Įmonės paieška'
    
    if ((isTargetSelector || isEmpty) && hiddenInput && placeholder) {
      // Auto-select the newly created company
      placeholder.textContent = companyName
      placeholder.classList.remove('text-gray-500')
      placeholder.classList.add('text-black', 'font-medium')
      hiddenInput.value = companyId
      
      const companyField = this.element.closest('.company-field')
      if (companyField) {
        companyField.dataset.verified = 'true'
        companyField.dataset.hasCompany = 'true'
      }
      
      // Close the dropdown
      this.hideDropdown()
      
      // Clear the search input
      if (this.inputTarget) {
        this.inputTarget.value = ''
      }
      
      // Refresh custom fields section
      const customFieldsFrame = document.getElementById('custom_fields')
      if (customFieldsFrame) {
        const currentUrl = new URL(window.location.href)
        const params = new URLSearchParams(currentUrl.search)
        params.set(hiddenInput.name.replace(/.*\[(.*)\]/, '$1'), companyId)
        
        const frameUrl = `${currentUrl.pathname}?${params.toString()}`
        customFieldsFrame.setAttribute('src', frameUrl)
      }
      
      // If this was the target selector, prevent other selectors from auto-selecting
      if (isTargetSelector) {
        event.stopImmediatePropagation()
      }
    }
  }

  debounce(func, wait) {
    let timeout
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout)
        func(...args)
      }
      clearTimeout(timeout)
      timeout = setTimeout(later, wait)
    }
  }
}
